// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The leaderboard. Every ANZSIC subdivision, all five measures, sortable,
// searchable, and every row opens its own record.

import { sparkline } from './charts';
import { card, note, rowActivationAttrs, segmented, viewHead } from './ui';
import { gloss } from '../glossary';
import { esc, money, num, period, rate, tipAttr, weeks } from '../format';
import { PHYSICAL, divisionColor } from '../colors';
import { withDivision } from '../data';
import { median } from '../analysis';
import { openIndustryDrawer } from './industryDrawer';
import type { Bundle, ClaimRow, Val } from '../types';

export const title = 'Industries';

type Row = ClaimRow & { division: string; divisionName: string };

let sortKey: 'name' | 'count' | 'freq' | 'inc' | 'comp' | 'lost' = 'freq';
let sortDir: 'asc' | 'desc' = 'desc';
let query = '';
let level: 'sub' | 'div' = 'sub';

function value(r: Row, key: typeof sortKey, idx: Record<string, number>): Val {
  if (key === 'name') return null;
  const series = r[key];
  return series ? series[idx[key]] ?? null : null;
}

function lastIdx(rows: Row[], key: 'count' | 'freq' | 'inc' | 'comp' | 'lost', periods: string[]): number {
  for (let i = periods.length - 1; i >= 0; i--) if (rows.some((r) => r[key]?.[i] !== null && r[key]?.[i] !== undefined)) return i;
  return periods.length - 1;
}

export function render(b: Bundle): string {
  const P = b.claims.periods;
  const all = withDivision(b.claims.industry);
  const divisions = b.claims.industry.filter((r) => r.level === 1 && !r.total).map((r) => ({ ...r, division: r.code, divisionName: r.name })) as Row[];
  const source = level === 'sub' ? all : divisions;

  const idx: Record<string, number> = {
    count: lastIdx(source, 'count', P),
    freq: lastIdx(source, 'freq', P),
    inc: lastIdx(source, 'inc', P),
    comp: lastIdx(source, 'comp', P),
    lost: lastIdx(source, 'lost', P),
  };

  const q = query.trim().toLowerCase();
  const filtered = source.filter((r) => !q || r.name.toLowerCase().includes(q) || r.divisionName.toLowerCase().includes(q));

  const sorted = [...filtered].sort((a, c) => {
    if (sortKey === 'name') return sortDir === 'asc' ? a.name.localeCompare(c.name) : c.name.localeCompare(a.name);
    const av = value(a, sortKey, idx);
    const cv = value(c, sortKey, idx);
    // Suppressed values sort last in both directions — an NP is not a zero and
    // must never take the top of a "safest industry" list.
    if (av === null && cv === null) return 0;
    if (av === null) return 1;
    if (cv === null) return -1;
    return sortDir === 'asc' ? av - cv : cv - av;
  });

  const freqValues = source.map((r) => r.freq?.[idx.freq] ?? null).filter((v): v is number => v !== null);
  const medFreq = median(freqValues);

  const th = (key: typeof sortKey, label: string, num2 = true) =>
    `<th scope="col" class="sortable${num2 ? ' num' : ''}"${key === sortKey ? ` aria-sort="${sortDir === 'asc' ? 'ascending' : 'descending'}"` : ''}><button type="button" class="th-sort" data-sort="${key}">${esc(label)}${key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}</button></th>`;

  return `
    ${viewHead(
      'Industries',
      'How dangerous is the industry I work in?',
      `Every Australian industry, ranked on the measures that matter: how often it injures people, what those injuries cost, and how long people are off work. The default sort is the ${gloss('frequency rate', 'frequency rate')}, not the claim count — a raw count just re-ranks industries by how many people they employ.`,
    )}

    <div class="ctl-row">
      ${segmented('level', [
        { value: 'sub', label: 'Subdivisions' },
        { value: 'div', label: 'Divisions' },
      ], level)}
      <label class="sel-lab" for="ind-search">Search</label>
      <input type="search" id="ind-search" placeholder="Try “construction” or “nursing”" value="${esc(query)}" />
      <span class="ctl-note">${sorted.length} of ${source.length} shown · rates from ${period(P[idx.freq])} · costs from ${period(P[idx.comp])}</span>
    </div>

    ${card(
      level === 'sub' ? 'Every industry subdivision' : 'The nineteen ANZSIC divisions',
      `Colour-coded against the median industry (${rate(medFreq, 2)} claims per million hours). Click any row for its full record.`,
      `<div class="tbl-wrap"><table class="data" style="min-width:880px">
        <thead><tr>
          ${th('name', 'Industry', false)}
          <th scope="col">Division</th>
          ${th('count', 'Claims')}
          ${th('freq', 'Per M hours')}
          ${th('inc', 'Per 1,000')}
          ${th('comp', 'Median paid')}
          ${th('lost', 'Time lost')}
          <th scope="col">Ten years</th>
        </tr></thead>
        <tbody>${sorted
          .map((r) => {
            const f = r.freq?.[idx.freq] ?? null;
            const tone = f === null ? '' : f > medFreq * 1.5 ? ' bad' : f > medFreq ? ' warn' : ' ok';
            return `<tr class="clickable" data-code="${esc(r.code)}" data-level="${level}"${rowActivationAttrs(`${r.name}, ${r.divisionName}. Open the full record.`)} data-tip="${tipAttr(`${r.name}\n${r.divisionName}\nClick for the full record`)}">
              <td class="wide">${esc(r.name)}</td>
              <td><span class="swatch-inline" style="background:${divisionColor(r.division)}"></span>${esc(r.divisionName.length > 26 ? `${r.divisionName.slice(0, 25)}…` : r.divisionName)}</td>
              <td class="num">${num(r.count[idx.count])}</td>
              <td class="num${tone}">${rate(f, 2)}</td>
              <td class="num">${rate(r.inc?.[idx.inc] ?? null, 1)}</td>
              <td class="num">${money(r.comp?.[idx.comp] ?? null)}</td>
              <td class="num">${weeks(r.lost?.[idx.lost] ?? null)}</td>
              <td>${sparkline(r.count, PHYSICAL, 96, 24)}</td>
            </tr>`;
          })
          .join('')}</tbody></table></div>`,
    )}

    ${note(
      `<strong>Reading the colours.</strong> A red frequency rate means more than 1.5× the median industry, amber means above the median, grey means at or below it. A dash means Safe Work Australia ${gloss('withheld the value', 'np')} — usually because there were fewer than five claims. It is not a zero, and rows with a dash are sorted to the bottom rather than the top.`,
    )}
  `;
}

export function mount(root: HTMLElement, b: Bundle, rerender: () => void): void {
  root.querySelectorAll<HTMLButtonElement>('[data-seg="level"]').forEach((btn) => {
    btn.addEventListener('click', () => { level = btn.dataset.value === 'div' ? 'div' : 'sub'; rerender(); });
  });

  root.querySelectorAll<HTMLButtonElement>('.th-sort').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.sort as typeof sortKey;
      if (key === sortKey) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else { sortKey = key; sortDir = key === 'name' ? 'asc' : 'desc'; }
      rerender();
    });
  });

  const search = root.querySelector<HTMLInputElement>('#ind-search');
  if (search) {
    let timer: number | undefined;
    search.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        query = search.value;
        rerender();
        const again = document.querySelector<HTMLInputElement>('#ind-search');
        if (again) { again.focus(); again.setSelectionRange(again.value.length, again.value.length); }
      }, 300);
    });
  }

  root.querySelectorAll<HTMLTableRowElement>('tr.clickable[data-code]').forEach((tr) => {
    // Enter and Space, so the row is not mouse-only.
    tr.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tr.click(); }
    });
    tr.addEventListener('click', () => {
      const code = tr.dataset.code ?? '';
      const rows = tr.dataset.level === 'div'
        ? b.claims.industry.filter((r) => r.level === 1 && !r.total).map((r) => ({ ...r, division: r.code, divisionName: r.name }))
        : withDivision(b.claims.industry);
      const row = rows.find((r) => r.code === code);
      if (row) openIndustryDrawer(b, row as Row);
    });
  });
}
